import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import TabRepondre from '../tabs/TabRepondre';
import { apiGet, apiPost, apiPut } from '../utils/api-client';
import { useToast } from '../components/Toast';

/**
 * TabRepondreContainer — Sprint 1
 *
 * Endpoints branchés :
 *  - GET  /api/dashboard/google-reviews?limit=50         → liste à répondre
 *  - GET  /api/gmb/status                                → bool connexion GMB
 *  - POST /api/dashboard/review-replies/generate         → Claude Haiku
 *  - PUT  /api/dashboard/review-replies/:replyId         → publish/draft
 *  - POST /api/gmb/auth/start                            → OAuth GMB redirect
 */
export default function TabRepondreContainer() {
  const { t } = useTranslation('tabs');
  const [reviews, setReviews]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [gmbConnected, setGmbConnected] = useState(false);
  const toast = useToast();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [reviewsRes, gmbRes] = await Promise.all([
        apiGet('/api/dashboard/google-reviews?limit=50').catch(() => null),
        apiGet('/api/gmb/status').catch(() => ({ connected: false })),
      ]);
      const list = reviewsRes?.reviews || reviewsRes?.data || reviewsRes || [];
      setReviews(Array.isArray(list) ? list : []);
      setGmbConnected(!!gmbRes?.connected);
    } catch (e) {
      console.error('[TabRepondre] load error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  /** Génère une réponse via Claude Haiku → renvoie le texte au composant */
  const handleGenerate = useCallback(async (review) => {
    try {
      const data = await apiPost('/api/dashboard/review-replies/generate', {
        review_id:     review.id ?? review.reviewId,
        author_name:   review.author,
        review_text:   review.text,
        review_rating: review.rating,
      });
      return data?.reply_text || data?.text || data?.draft || '';
    } catch (e) {
      alert(t('repondre.alerts.ia_generate_failed', { error: e.message }));
      return '';
    }
  }, [t]);

  /** Publie la réponse sur Google (ou sauvegarde en brouillon si non connecté) */
  const handlePublish = useCallback(async (review, text) => {
    try {
      const replyId = review.replyId || review.reply_id || review.id;
      await apiPut(`/api/dashboard/review-replies/${replyId}`, {
        reply_text: text,
        action: 'publish',
      });
      await loadData(); // refetch pour voir le statut "publié"
      return true;
    } catch (e) {
      alert(t('repondre.alerts.publish_failed', { error: e.message }));
      return false;
    }
  }, [t, loadData]);

  /** Sauvegarde un brouillon sans publier (action: 'save_draft' côté backend) */
  const handleSaveDraft = useCallback(async (review, text) => {
    const trimmed = (text || '').trim();
    if (!trimmed) {
      toast.warning(t('repondre.alerts.draft_empty'));
      return false;
    }
    try {
      const replyId = review.replyId || review.reply_id || review.id;
      await apiPut(`/api/dashboard/review-replies/${replyId}`, {
        reply_text: trimmed,
        action: 'save_draft',
      });
      toast.success(t('repondre.alerts.draft_saved'));
      return true;
    } catch (e) {
      toast.error(t('repondre.alerts.draft_save_failed', { error: e.message }));
      return false;
    }
  }, [t, toast]);

  /** Lance le flux OAuth Google Business Profile */
  const handleConnectGMB = useCallback(async () => {
    try {
      const data = await apiPost('/api/gmb/auth/start');
      if (data?.url) {
        window.location.href = data.url;
      } else {
        alert(t('repondre.alerts.gmb_url_missing'));
      }
    } catch (e) {
      alert(t('repondre.alerts.gmb_connect_failed', { error: e.message }));
    }
  }, [t]);

  return (
    <TabRepondre
      reviews={reviews}
      loading={loading}
      gmbConnected={gmbConnected}
      onGenerate={handleGenerate}
      onPublish={handlePublish}
      onSaveDraft={handleSaveDraft}
      onConnectGMB={handleConnectGMB}
    />
  );
}
